import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Menu, X } from 'lucide-react'

import Logo from '../../../assets/logos/logo.png'
import GroupIcon from '../../../assets/images/icons/Group.svg'
import allservice from '../../../assets/images/developmentlanguage/allservice.webp'
import frontend from '../../../assets/images/developmentlanguage/frontendicon.webp'
import database from '../../../assets/images/developmentlanguage/databaseicon.webp'
import backend from '../../../assets/images/developmentlanguage/backendicon.webp'
import automation from '../../../assets/images/developmentlanguage/automationicon.webp'
import clouddevops from '../../../assets/images/developmentlanguage/clouddevopsicon.webp'
import { trackClarityEvent } from "../../../app/utils/ClarityAnalytics";
import { trackEvent } from "../../../app/utils/firebaseEvents";
import { CLARITY_EVENTS } from "../../../app/utils/EventTypes";

import Button from '../../atoms/Button/Button'

const serviceNavItems = [
  { name: 'All Services', icon: allservice, path: '/service' },
  { name: 'Frontend Development', icon: frontend, path: '/frontend' },
  { name: 'AI & Automation', icon: automation, path: '/automation' },
  { name: 'Backend Development', icon: backend, path: '/backend' },
  { name: 'Database', icon: database, path: '/database' },
  { name: 'Cloud & DevOps', icon: clouddevops, path: '/clouddevops' },
] as const

type HeaderProps = {
  textColor?: string
}

const Header = ({ textColor = 'text-white' }: HeaderProps = {}) => {
  const navigate = useNavigate()

  const [showServices, setShowServices] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileServiceOpen, setMobileServiceOpen] = useState(false)

  const mobileMenuRef = useRef<HTMLDivElement | null>(null)
  const desktopServiceRef = useRef<HTMLDivElement | null>(null)


  const handlegetintouch = () => {
    const payload = {
      source: 'portfolio_section',
      page: window.location.pathname,
    };

    trackClarityEvent(CLARITY_EVENTS.VIEW_ALL_OUR_PROJECTS, payload);
    trackEvent(CLARITY_EVENTS.VIEW_ALL_OUR_PROJECTS, payload);

    navigate('/portfolio');
  };


  /* -------------------- OUTSIDE CLICK (mobile menu) -------------------- */
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node)
      ) {
        setMobileMenuOpen(false)
        setMobileServiceOpen(false)
      }
    }
    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleOutsideClick)
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [mobileMenuOpen])

  /* -------------------- OUTSIDE CLICK (desktop service dropdown) -------------------- */
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        desktopServiceRef.current &&
        !desktopServiceRef.current.contains(event.target as Node)
      ) {
        setShowServices(false)
      }
    }
    if (showServices) {
      document.addEventListener('mousedown', handleOutsideClick)
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [showServices])

  /* -------------------- NAVIGATION -------------------- */
  const handleNavigate = (path: string) => {
    navigate(path)
    setMobileMenuOpen(false)
    setMobileServiceOpen(false)
    setShowServices(false)
  }

  return (
    <div className="relative z-[1000]">

      {/* ==================== NAVBAR ==================== */}
      <div className="flex justify-center">
        <div
          className="
            mt-6 flex w-[95%] max-w-[2000px] items-center justify-between
            rounded-[20px] border border-purple bg-white/4 px-4 py-2
            backdrop-blur-[70px] shadow-lg
            md:mt-8 md:w-[92%] md:px-5 md:py-5
            lg:mt-10 lg:w-[90%] lg:px-6 lg:py-6
          "
        >

          {/* ==================== LOGO ==================== */}
          <img
            src={Logo}
            alt="logo"
            className="
              h-14 w-14 cursor-pointer rounded-full bg-[#815eff]/40 object-cover
              md:h-16 md:w-16 lg:h-20 lg:w-20
            "
            onClick={() => handleNavigate('/')}
          />

          {/* ==================== RIGHT SECTION ==================== */}
          <div className="flex items-center gap-4 md:gap-8 lg:gap-12 xl:gap-20">

            {/* ==================== DESKTOP MENU ==================== */}
            <div
              className={`
                hidden items-center gap-4 text-base ${textColor}
                font-gilroy-medium lg:flex lg:gap-5 lg:text-lg xl:gap-6 xl:text-xl
              `}
            >
              <span onClick={() => handleNavigate('/')} className="cursor-pointer">
                Home
              </span>

              <span onClick={() => handleNavigate('/aboutus')} className="cursor-pointer">
                About us
              </span>

              {/* SERVICES — click to toggle */}
              <div
                ref={desktopServiceRef}
                className="relative inline-flex"
              >
                <button
                  type="button"
                  onClick={() => setShowServices((prev) => !prev)}
                  className="flex cursor-pointer items-center gap-2 py-1 select-none bg-transparent border-none outline-none text-inherit font-inherit text-[length:inherit]"
                >
                  Service
                  <img
                    src={GroupIcon}
                    alt=""
                    className={`h-4 w-4 transition-transform duration-300 ${showServices ? 'rotate-180' : 'rotate-0'}`}
                  />
                </button>

                {/* DESKTOP DROPDOWN */}
                <div
                  className={`
                    absolute left-0 top-full z-[1001] mt-3
                    w-[min(520px,calc(100vw-2rem))]
                    transform transition-all duration-300 ease-in-out
                     ${
      showServices
        ? 'visible opacity-100 scale-100 translate-y-0'
        : 'invisible opacity-0 scale-95 -translate-y-2 pointer-events-none'
    }
                  `}
                >
                  <div className="rounded-2xl bg-[#0B071D]/90 p-3 shadow-2xl">
                    <div className="grid grid-cols-2 gap-2">
                      {/* INDIVIDUAL SERVICES */}
                      {serviceNavItems.map((item, index) => (
                        <div
                          key={index}
                          className="
                            flex cursor-pointer items-center gap-3 rounded-xl
                            px-3 py-2 transition-all duration-200
                            hover:scale-[1.02] hover:bg-[#744DFF12]
                          "
                          onClick={() => handleNavigate(item.path)}
                        >
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#100a29]">
                            <img src={item.icon} alt="" className="h-5 w-5 object-contain" />
                          </div>
                          <span className="text-sm text-white md:text-base">{item.name}</span>
                        </div>
                      ))}

                    </div>
                  </div>
                </div>
              </div>

              <span onClick={() => handleNavigate('/portfolio')} className="cursor-pointer">
                Portfolio
              </span>

              <span onClick={() => handleNavigate('/technology')} className="cursor-pointer">
                Technology
              </span>

              <span onClick={() => handleNavigate('/career')} className="cursor-pointer">
                Career
              </span>

              <span onClick={() => handleNavigate('/upskill')} className="cursor-pointer">
                Upskill
              </span>
            </div>

            {/* ==================== DESKTOP BUTTON ==================== */}
            <div className="hidden lg:block">
              <Button
                className="font-gilroy-medium text-sm lg:text-lg"
                onClick={() => handleNavigate('/contactus')}
              >
                Get in Touch
              </Button>
            </div>

            {/* ==================== MOBILE/TABLET ICON ==================== */}
            <button
              className="text-white lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={32} /> : <Menu size={32} />}
            </button>
          </div>
        </div>
      </div>

      {/* ==================== MOBILE/TABLET MENU ==================== */}
      <div
        ref={mobileMenuRef}
        className={`
          absolute left-1/2 top-full z-[999] mt-4 w-[95%] h-[300px]
          -translate-x-1/2 rounded-2xl border border-purple
          bg-[#261e47]/95 backdrop-blur-xl overflow-hidden lg:hidden
          transition-all duration-400 ease-in
          ${
            mobileMenuOpen
              ? 'visible opacity-100 translate-y-0 scale-100'
              : 'invisible opacity-0 translate-y-0 scale-100 pointer-events-none'
          }
        `}
      >
        {/* SCROLL AREA */}
        <div className="h-full overflow-y-auto scrollbar-hide">
          <div
            className={`
              flex flex-col gap-3 px-6 py-6 text-white font-gilroy-medium
              transition-all duration-500 ease-in-out
              ${mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
            `}
          >
            <span
              onClick={() => handleNavigate('/')}
              className="cursor-pointer text-lg transition-all duration-300 hover:translate-x-2"
            >
              Home
            </span>

            <span
              onClick={() => handleNavigate('/aboutus')}
              className="cursor-pointer text-lg transition-all duration-300 hover:translate-x-2"
            >
              About us
            </span>

            {/* SERVICES */}
            <div className="flex flex-col">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setMobileServiceOpen(!mobileServiceOpen)}
              >
                <span className="text-lg">Services</span>
                <img
                  src={GroupIcon}
                  alt=""
                  className={`h-4 w-4 transition-all duration-500 ${mobileServiceOpen ? 'rotate-180' : 'rotate-0'}`}
                />
              </div>

              {/* SERVICE DROPDOWN */}
              <div
                className={`
                  overflow-hidden transition-all duration-600 ease-in
                  ${mobileServiceOpen ? 'mt-3 max-h-[600px] opacity-100' : 'mt-0 max-h-0 opacity-0'}
                `}
              >
                <div className="flex flex-col gap-3">

                  {/* INDIVIDUAL SERVICES */}
                  {serviceNavItems.map((item, index) => (
                    <div
                      key={index}
                      className={`
                        flex cursor-pointer items-center gap-3 rounded-xl
                        bg-white/5 px-3 py-3 transition-all duration-300
                        hover:bg-[#744DFF20]
                        ${mobileServiceOpen ? 'translate-x-0 opacity-100' : 'translate-x-5 opacity-0'}
                      `}
                      style={{ transitionDelay: mobileServiceOpen ? `${(index + 1) * 60}ms` : '0ms' }}
                      onClick={() => handleNavigate(item.path)}
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#261e47]/95">
                        <img src={item.icon} alt="" className="h-5 w-5 object-contain" />
                      </div>
                      <span className="text-sm">{item.name}</span>
                    </div>
                  ))}

                </div>
              </div>
            </div>

            <span
              onClick={() => handleNavigate('/portfolio')}
              className="cursor-pointer text-lg transition-all duration-300 hover:translate-x-2"
            >
              Portfolio
            </span>

            <span
              onClick={() => handleNavigate('/technology')}
              className="cursor-pointer text-lg transition-all duration-300 hover:translate-x-2"
            >
              Technology
            </span>

            <span
              onClick={() => handleNavigate('/career')}
              className="cursor-pointer text-lg transition-all duration-300 hover:translate-x-2"
            >
              Career
            </span>

            <span
              onClick={() => handleNavigate('/upskill')}
              className="cursor-pointer text-lg transition-all duration-300 hover:translate-x-2"
            >
              Upskill
            </span>

            <div className="flex justify-center pt-2">
              <Button
                className="w-[60%] font-gilroy-medium transition-all duration-300 hover:scale-105"
                onClick={handlegetintouch}
              >
                Get in Touch
              </Button>
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}

export default Header